import {CAMPAIGN_KEYS} from "@/api/campaignApi";
import {CAMPAIGN_APPLICATION_KEYS} from "@/api/campaignApplicationApi";
import HeartOutlineIcon from "@/assets/colorable/heart_outline.svg?react";
import HeartIcon from "@/assets/dark_gradient/heart.svg?react";
import {client} from "@/httpClient";
import clsxm from "@/lib/clsxm";
import {Can} from "@/permissions";
import {queryClient} from "@/queryClient";
import {useCampaignGalleryStore} from "@/store/campaignGallery";
import useCampaignInfluencerFilterStore from "@/store/campaignInfluencerFilter";
import useCampaignManagementStore from "@/store/campaignManagement";
import {useCarouselStore} from "@/store/carousel";
import type {TContent, TInstagramMedia} from "@/store/types";
import {useMutation} from "@tanstack/react-query";
import {useState, type FC} from "react";

const MEDIA_TYPES: Record<TContent, "INSTAGRAM" | "SOURCE" | "TIKTOK" | "UGC"> = {
  FEED: "INSTAGRAM",
  INSIGHTS: "INSTAGRAM",
  POST: "INSTAGRAM",
  REEL: "INSTAGRAM",
  REELS: "INSTAGRAM",
  SOURCE: "SOURCE",
  STORY: "INSTAGRAM",
  TIKTOK: "TIKTOK",
  UGC: "UGC",
};

const ContentLikeButton: FC<{
  content: {instagram_media: TInstagramMedia | null};
  className?: string;
  contentType?: TContent | null;
}> = ({content, className, contentType}) => {
  const campaign =
    content?.instagram_media?.campaign_application?.campaign ?? useCampaignManagementStore(state => state.campaign);
  const campaignType = campaign.ugc ? "ugc" : campaign.tiktok ? "tiktok" : "instagram";
  const dateFilter = useCampaignManagementStore(state => state.reportingDateFilter);
  const gallerySearchParams = useCampaignGalleryStore(state => state.searchParams);
  const searchParam = useCampaignInfluencerFilterStore(state => state.filters.search);
  const toggleLikedStatus = useCarouselStore(state => state.toggleLikedStatus);
  const [isLiked, setLiked] = useState(content.instagram_media?.liked);
  const resolvedContentType = contentType ?? content.instagram_media?.media_product_type;
  const mediaType = (resolvedContentType && MEDIA_TYPES[resolvedContentType]) || campaignType.toUpperCase();

  const {mutate: toggledLikedStatus, isPending} = useMutation({
    meta: {skipSuccessToast: true},
    mutationKey: CAMPAIGN_APPLICATION_KEYS.mApplicationLikedMedia,
    mutationFn: () =>
      client.post(CAMPAIGN_APPLICATION_KEYS.mApplicationLikedMedia[0], {
        media_id: content.instagram_media?.id,
        media_type: mediaType,
      }),
    onMutate: () => {
      setLiked(prev => !prev);
      toggleLikedStatus(content.instagram_media?.id!);
    },
    onSuccess: () => {
      [
        CAMPAIGN_KEYS.qCampaignReportingApplications({campaignId: campaign.id, dateFilter, searchParam}),
        CAMPAIGN_KEYS.qCampaignFeaturedContent(campaign.id, dateFilter),
        CAMPAIGN_KEYS.qCampaignGallery(campaign.id, gallerySearchParams),
        CAMPAIGN_KEYS.qCampaignActiveApplications(campaign.id, searchParam),
      ].map(queryKey => queryClient.invalidateQueries({queryKey, refetchType: "none"}));
    },
    onError: () => {
      setLiked(prev => !prev);
      toggleLikedStatus(content.instagram_media?.id!);
    },
  });

  return (
    <Can I="create" a="FavoriteContentLike">
      <button
        onClick={e => {
          e.stopPropagation();
          toggledLikedStatus();
        }}
        className={clsxm(
          "border-neutral-white-dark hover:bg-neutral-white-dark absolute top-0 right-0 z-50 inline-block -translate-x-2 translate-y-2 items-center justify-center rounded-full border bg-white p-2 shadow-xs",
          className,
        )}
        disabled={isPending}>
        {isLiked ? (
          <HeartIcon className="size-4.5 pt-0.5" />
        ) : (
          <HeartOutlineIcon className="text-primary-dark-solid size-4.5 pt-0.5" />
        )}
      </button>
    </Can>
  );
};

export default ContentLikeButton;
